import { HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { catchError, map, throwError } from "rxjs";
import { Observable } from "rxjs/internal/Observable";
import { GetAddressesRequestModel } from "@address-models/get-addresses-request.model";
import { GetAddressesResponse } from "@address-models/get-address-response.model";
import { PremiseHazardType } from "@premise-hazard-type-models/premise-hazard-type.model";
import { GetPremiseHazardTypesRequestModel } from "@premise-hazard-type-models/get-premise-hazard-type-request.model";
import { GetPremiseHazardTypesResponse } from "@premise-hazard-type-models/get-premise-hazard-types-response.model";
import { PremiseHazard } from "@premise-hazard-models/premise-hazard.model";
import { GetPremiseHazardRequestModel } from "@premise-hazard-models/get-premise-hazard-request.model";
import { GetPremiseHazardsRequestModel } from "@premise-hazard-models/get-premisehazards-request.model";
import { CcDriveSearchByPathResponse } from "@premise-hazard-models/cc-drive-search-by-path-response.model";
import { GetPremiseHazardsResponse } from "@premise-hazard-models/get-premise-hazards-response.model";
import { ResponseItem } from "@models/response-item.model";
import { PremiseHazardRestBase } from "./premise-hazard-rest-base";

@Injectable({
  providedIn: 'root',
})

export class PremiseHazardQueryRestService extends PremiseHazardRestBase {
  public getPremiseHazards({ requestOptions }: { requestOptions: GetPremiseHazardsRequestModel; }): Observable<GetPremiseHazardsResponse> {
    let params = this.createHttpParams(requestOptions);
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      params: params,
    };

    let url = `${this.phzBaseUrl}${this.queryUrl}${this.addCustomerRoute()}/`;

    return this.httpClient.get<GetPremiseHazardsResponse>(url, opt)
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            this.handleHttpError(httpError);
          }
          return throwError(() => err);
        })
      )
  }

  public getAddresses(requestOptions: GetAddressesRequestModel): Observable<GetAddressesResponse> {
    let params = this.createHttpParams(requestOptions);
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      params: params,
    };

    let url = `${this.phzBaseUrl}${this.queryUrl}${this.addCustomerRoute()}/addresses`;

    return this.httpClient.get<GetAddressesResponse>(url, opt)
      .pipe(
        catchError((err => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            this.handleHttpError(httpError);
          }
          return throwError(() => err);
        })));
  }

  public getPremiseHazardType(id: string): Observable<ResponseItem<PremiseHazardType>> {
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
    }

    let url = `${this.phzBaseUrl}${this.queryUrl}${this.addCustomerRoute()}/types/${id}`;

    return this.httpClient.get<PremiseHazardType>(url, opt)
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            this.handleHttpError(httpError);
          }
          return throwError(() => err);
        }),
        map((response) => {
          const result: ResponseItem<PremiseHazardType> = {
            Item: response.body!,
            ETag: response.headers.get('ETag')!,
          }
          return result;
        })
      );
  }

  public getPremiseHazard(id: string, requestOptions: GetPremiseHazardRequestModel, suppressNotFoundToast: boolean = false): Observable<ResponseItem<PremiseHazard>> {
    let params = this.createHttpParams(requestOptions);
    
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      observe: 'response' as 'response',
      params: params,
    };

    let url = `${this.phzBaseUrl}${this.queryUrl}${this.addCustomerRoute()}/${id}`;

    return this.httpClient.get<PremiseHazard>(url, opt)
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            if (!(suppressNotFoundToast && httpError.status === 404)) {
              this.handleHttpError(httpError);
            }
          }
          return throwError(() => err);
        }),
        map((response) => {
          const result: ResponseItem<PremiseHazard> = {
            Item: response!.body!,
            ETag: response.headers!.get('ETag')!,
          }
          return result;
        })
      );
  }

  public getPremiseHazardTypes(requestOptions: GetPremiseHazardTypesRequestModel): Observable<GetPremiseHazardTypesResponse> {
    let params = this.createHttpParams(requestOptions);
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
        'content-type': 'application/json',
      }),
      params: params,
    };

    let url = `${this.phzBaseUrl}${this.queryUrl}${this.addCustomerRoute()}/types`;

    return this.httpClient.get<GetPremiseHazardTypesResponse>(url, opt)
      .pipe(
        catchError(((err) => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            this.handleHttpError(httpError);
          }
          return throwError(() => err);
        })));
  }

  public getAttachment(premiseHazardId: string, attachmentId: string): Observable<CcDriveSearchByPathResponse> {
    const opt = {
      headers: new HttpHeaders({
        'MsiProduct': this.MsiProduct,
      }),
    };

    let url = `${this.phzBaseUrl}${this.queryUrl}/premisehazards/${premiseHazardId}/attachments/${attachmentId}`;

    return this.httpClient.get<CcDriveSearchByPathResponse>(url, opt)
      .pipe(
        catchError(err => {
          if (err instanceof HttpErrorResponse) {
            let httpError = err as HttpErrorResponse;
            this.handleHttpError(httpError);
          }

          return throwError(() => err);
        })
    )
  }
}
